import { useQuery } from "@tanstack/react-query";
import { ChevronsUpDown, Download, LogOut, Monitor, Moon, Search, Sun, Tv } from "lucide-react";
import { useTheme } from "next-themes";
import type { ComponentProps } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { api } from "../api";
import { useSession } from "../auth";
import { initials } from "../lib/format";
import { countQuery, ISSUE_TABS } from "../lib/issue-views";
import { actorName } from "../lib/people";
import { useIsAdmin } from "../lib/permissions";
import { useInbox } from "../pages/InboxPage";
import { useInstall } from "../pwa";
import { useFactory } from "../store";
import { BrandMark, Wordmark } from "./brand";
import { SearchShortcut, useCommandMenu } from "./command-menu";
import { ALERT_BADGES, type BadgeKey, type NavItem, NOTIFICATIONS, useRoleNav } from "./nav";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "./ui/sidebar";

/** The Human required tab's own badge under Mine, so the two counts agree. */
const WAITING_ON_ME = countQuery(
  ISSUE_TABS.find((tab) => tab.value === "human")!,
  new URLSearchParams(),
  "mine",
);

/**
 * Counts on the nav: work orders needing a human, work orders on the line, unread
 * notifications, and issues waiting on the signed-in person.
 */
export function useNavBadges(): Record<BadgeKey, number> {
  const { state } = useFactory();
  const inbox = useInbox();
  const waiting = useQuery({
    queryKey: ["issues", "counts", [WAITING_ON_ME]],
    queryFn: () => api.issueCounts([WAITING_ON_ME]),
    refetchInterval: 30_000,
  });
  const attention = state
    ? state.kpis.blocked +
      state.kpis.waitingForApproval +
      (state.kpis.waitingForHuman ?? 0) +
      state.runs.filter((r) => r.status === "waiting_for_input").length
    : 0;
  return {
    attention,
    inProduction: state?.kpis.inProduction ?? 0,
    inbox: inbox.data?.comments.length ?? 0,
    waiting: waiting.data?.counts[0] ?? 0,
  };
}

export function AppSidebar(props: ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const { setOpenMobile } = useSidebar();
  const admin = useIsAdmin();
  const nav = useRoleNav();
  const badges = useNavBadges();

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <NavLink
          to={admin ? "/" : "/inbox"}
          aria-label="Skynet home"
          onClick={() => setOpenMobile(false)}
          className="flex h-12 items-center gap-3 rounded-md px-2 text-sidebar-foreground outline-hidden ring-sidebar-ring focus-visible:ring-2"
        >
          <BrandMark />
          <Wordmark />
        </NavLink>
        <SearchButton />
      </SidebarHeader>
      <SidebarContent className="gap-1 pb-3">
        <SidebarGroup className="py-1">
          <SidebarGroupContent>
            <NavLinks items={[NOTIFICATIONS]} pathname={location.pathname} badges={badges} />
          </SidebarGroupContent>
        </SidebarGroup>
        {nav.map((group) => (
          <SidebarGroup key={group.label} className="py-1">
            <SidebarGroupLabel className="h-6 text-[11px] font-semibold tracking-[0.06em] text-sidebar-foreground/60 uppercase">
              {group.label}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <NavLinks items={group.items} pathname={location.pathname} badges={badges} />
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}

/** Opens global search. Looks like a field so it reads as "type here", and shows the shortcut. */
function SearchButton() {
  const { setOpenMobile } = useSidebar();
  const { setOpen } = useCommandMenu();
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          onClick={() => {
            setOpenMobile(false);
            setOpen(true);
          }}
          className={cn(
            "h-8 gap-2.5 border border-sidebar-border bg-background text-sidebar-foreground/60 shadow-[0_1px_2px_rgb(0_0_0/0.04)] pointer-coarse:h-10 pointer-coarse:text-[15px]",
            "hover:text-sidebar-foreground [&_svg]:text-sidebar-foreground/50 dark:bg-sidebar-accent/40",
          )}
        >
          <Search />
          <span>Search</span>
          <SearchShortcut className="ml-auto pointer-coarse:hidden" />
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

function NavLinks({
  items,
  pathname,
  badges,
}: {
  items: NavItem[];
  pathname: string;
  badges: Record<BadgeKey, number>;
}) {
  const { setOpenMobile } = useSidebar();

  return (
    <SidebarMenu>
      {items.map((item) => {
        const active = item.end ? pathname === item.to : pathname.startsWith(item.to);
        const count = item.badge ? badges[item.badge] : 0;
        return (
          <SidebarMenuItem key={item.to}>
            {/* The current page is a raised card on the sidebar surface; the rest stay flat and quiet. */}
            <SidebarMenuButton
              asChild
              isActive={active}
              tooltip={item.label}
              className={cn(
                "h-7.5 gap-2.5 text-sidebar-foreground/80 pointer-coarse:h-10 pointer-coarse:text-[15px] transition-[background-color,color,box-shadow] duration-(--motion-dur)",
                "[&_svg]:text-sidebar-foreground/50 hover:[&_svg]:text-sidebar-foreground/80",
                "data-active:bg-background data-active:text-sidebar-foreground data-active:shadow-[0_1px_2px_rgb(0_0_0/0.05),0_0_0_1px_var(--sidebar-border)] data-active:[&_svg]:text-sidebar-foreground",
                "dark:data-active:bg-sidebar-accent dark:data-active:shadow-none",
              )}
            >
              <NavLink to={item.to} end={item.end} onClick={() => setOpenMobile(false)}>
                <item.icon />
                <span>{item.label}</span>
              </NavLink>
            </SidebarMenuButton>
            {count > 0 && (
              <SidebarMenuBadge
                className={cn(
                  "top-1.5 h-4.5 min-w-4.5 rounded-full pointer-coarse:top-2.75 px-1.5 text-[11px]",
                  item.badge && ALERT_BADGES.has(item.badge)
                    ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                    : "text-muted-foreground",
                )}
              >
                {count}
              </SidebarMenuBadge>
            )}
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );
}

function NavUser() {
  const session = useSession();
  const { isMobile } = useSidebar();
  const { theme = "system", setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const installer = useInstall();
  const canSignOut = session.token !== undefined || !import.meta.env.DEV;
  const label = actorName(session.name) || actorName(session.email.split("@")[0]) || "You";

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="size-8 rounded-lg">
                <AvatarFallback className="rounded-lg bg-primary/10 text-xs font-medium text-primary">
                  {initials(label)}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{label}</span>
                <span className="truncate text-xs text-muted-foreground">{session.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="font-normal">
              <div className="grid text-left text-sm leading-tight">
                <span className="truncate font-medium">{label}</span>
                <span className="truncate text-xs text-muted-foreground">{session.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  {theme === "dark" ? <Moon /> : theme === "light" ? <Sun /> : <Monitor />}
                  Theme
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuRadioGroup value={theme} onValueChange={setTheme}>
                    <DropdownMenuRadioItem value="light">Light</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="dark">Dark</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="system">System</DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuItem
                onSelect={() => navigate("/tv", { state: { from: location.pathname + location.search } })}
              >
                <Tv />
                TV mode
              </DropdownMenuItem>
              {installer.kind && (
                <DropdownMenuItem
                  onSelect={() =>
                    installer.kind === "prompt"
                      ? void installer.install()
                      : toast("Install Skynet", {
                          description: "Tap the Share button in Safari, then Add to Home Screen.",
                        })
                  }
                >
                  <Download />
                  Install app
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
            {canSignOut && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={session.signOut}>
                  <LogOut />
                  Sign out
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
